import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useRemoteParams from "../../hooks/useRemoteParams";
import useTemplateInterpreter from "../../hooks/useTemplateInterpreter";
import { OnboardingType } from "../../types";
import { loadFavicon } from "../../utils";
import { updateToken } from "../../reducers/authReducer";
import { doc, getFirestore, onSnapshot } from "@firebase/firestore";
import { firestoreCollections } from "../../constants";
import { ThemeProvider } from "@allaria-tech/matter/ThemeProvider";
import useTheme from "../../hooks/useTheme";
import {
  onUpdateOnboardingType,
  onUpdateTheme,
} from "../../reducers/activeTemplateReducer";

const RemoteStep = () => {
  const {
    values: { onboardingType, uuid, token },
  } = useRemoteParams();

  const dispatch = useAppDispatch();

  const state = useAppSelector((state) => state);

  const {
    favicon,
    remoteSteps,
    steps,
    theme: templateTheme,
  } = state.activeTemplate;

  const path = window.location.pathname;
  const remoteStep = remoteSteps?.find((step) => step.link === path)?.step;
  const step = remoteStep?.component;
  const stepModals = remoteStep?.stepModals;

  const [tokenReady, setTokenReady] = useState(!!token);

  const { loadTemplate } = useTemplateInterpreter({
    dispatch,
    onboardingType: onboardingType as OnboardingType,
    state,
    draftUUID: uuid,
  });

  useEffect(() => {
    if (onboardingType) {
      dispatch(onUpdateOnboardingType(onboardingType));
    }
    loadTemplate();
  }, [uuid]);

  useEffect(() => {
    if (favicon) {
      loadFavicon(favicon);
    }
  }, [favicon]);

  useEffect(() => {
    if (steps && !templateTheme) dispatch(onUpdateTheme("ALLARIA"));
  }, [steps, templateTheme]);

  useEffect(() => {
    if (token) {
      dispatch(updateToken(token));
      return;
    }

    if (onboardingType !== "DIGITAL" || !uuid) return;

    const collection =
      path === "/dni"
        ? firestoreCollections.dniCrypto
        : firestoreCollections.livenessCrypto;

    const db = getFirestore();
    const unsub = onSnapshot(doc(db, collection, uuid), (snap) => {
      const remoteToken = snap.data()?.remoteToken;
      if (remoteToken) {
        dispatch(updateToken(remoteToken));
        setTokenReady(true);
        unsub();
      }
    });

    return unsub;
  }, [token, uuid]);

  const theme = useTheme();

  // Only the crypto remote step has to wait: its token never travels in the URL,
  // it arrives asynchronously over Firestore. The other onboardings get the token
  // as a query param, so it is already there on the first render and gating them
  // here would just turn a missing token into a permanently blank page.
  if (onboardingType === "DIGITAL" && !tokenReady) return <></>;

  const content = step ? (
    <>
      {stepModals}
      {step(dispatch, state)}
    </>
  ) : (
    <></>
  );

  // The ThemeProvider stays mounted so the step is not remounted when the
  // active template's theme arrives: only the content waits for the theme.
  return (
    <ThemeProvider theme={theme ?? {}}>{theme ? content : null}</ThemeProvider>
  );
};

export default RemoteStep;
